"use client";

import type { CSSProperties } from "react";
import type { DeviceId } from "@/components/landing/ResponsiveShowcase/types";
import DesktopDevice from "@/components/landing/ResponsiveShowcase/DesktopDevice";
import MobileDevice from "@/components/landing/ResponsiveShowcase/MobileDevice";
import {
	DEVICES,
	FLANK_BASE,
	slotFor,
} from "@/components/landing/ResponsiveShowcase/morphAnimation";
import TabletDevice from "@/components/landing/ResponsiveShowcase/TabletDevice";
import { cn } from "@/lib/utils";

interface DeviceStageProps {
	active: DeviceId;
	shopLabel: string;
	deviceLabels: Record<DeviceId, string>;
	onChange: (id: DeviceId) => void;
}

/**
 * All three previews stay mounted in fixed absolute slots and only ever animate
 * transform / opacity / shadow — never layout — so switching devices reads as
 * one coordinated hand-off rather than a rebuild. The flanking (non-active)
 * devices are real controls: clicking or tabbing to one promotes it to the
 * centre. A hidden desktop mock keeps the stage a stable height regardless of
 * which device is active, so nothing below the stage shifts. Motion, easing and
 * reduced-motion handling live in `.showcase-*` rules in globals.css.
 */
function DeviceStage(prop: DeviceStageProps) {
	const { active, shopLabel, deviceLabels, onChange } = prop;

	return (
		<div className="mt-14 px-6">
			<div
				className="showcase-stage relative mx-auto w-fit"
				style={{ "--flank-base": FLANK_BASE[active] } as CSSProperties}
			>
				{/* Soft radial glow that grounds the previews and follows the section. */}
				<div aria-hidden className="showcase-stage__glow" />

				{/* Invisible size anchor: the tallest/widest device holds the stage
            height so promoting a smaller device never collapses the section. */}
				<div aria-hidden className="invisible">
					<DesktopDevice shopLabel={shopLabel} />
				</div>

				{DEVICES.map((d) => {
					const slot = slotFor(d.id, active);
					const isActive = slot === 1;
					return (
						<button
							key={d.id}
							type="button"
							data-slot={slot}
							disabled={isActive}
							aria-current={isActive ? "true" : undefined}
							aria-label={isActive ? undefined : `Show the ${deviceLabels[d.id]} preview`}
							onClick={() => onChange(d.id)}
							className={cn(
								"showcase-stage__item appearance-none rounded-[1.6rem] border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-1)] focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
								isActive ? "cursor-default" : "cursor-pointer",
							)}
						>
							{d.id === "desktop" && <DesktopDevice shopLabel={shopLabel} />}
							{d.id === "tablet" && <TabletDevice shopLabel={shopLabel} />}
							{d.id === "mobile" && <MobileDevice shopLabel={shopLabel} />}
						</button>
					);
				})}
			</div>
		</div>
	);
}

export default DeviceStage;
